import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Customer } from '../dto/customer';
import { VesselListComponent } from "../../vessel/component/vessel-list/vessel-list.component";
import { StateNotifyService } from '../../../common/state/service/state-notify.service';
import { CustomerService } from '../service/customer.service';
import { ConfirmDialogComponent } from '../../../common/component/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'dpi-customer-details',
  imports: [MatTabsModule, VesselListComponent, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatCardModule,
    MatIconModule, MatButtonModule, MatToolbarModule, MatBadgeModule, MatTooltipModule, NgIf],
  templateUrl: './customer-details.component.html',
  styleUrl: './customer-details.component.scss'
})
export class CustomerDetailsComponent implements OnInit, OnDestroy {
  customer: Customer;
  isReadonly: boolean;
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component being destroyed
  isContentChanged: boolean;
  vesselNum: number;
  private _dialogConfirm = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private customerService: CustomerService, private router: Router) {
    this.customer = this.notifyService.getSelectedCustomer();
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;
    this.vesselNum = 0;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.customerSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.customer = model;
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });
  }

  onEditModeChange(isReadonly: boolean) {
    this.isReadonly = isReadonly;
    if (isReadonly) {
      this.fieldAppearance = 'outline';
    } else {
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  setVesselNum(count: number) {
    this.vesselNum = count;
  }
  // --- CRUD actions ---

  createCustomer() {
    this.customerService.createCustomer()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateCustomer() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.customerService.updateCustomer(this.customer)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.customer = model;
          this.notifyService.updateCustomerSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteCustomer() {
    this.customerService.deleteCustomer(this.customer.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeCustomerAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: Customer) {
    this.customer = model;
    this.notifyService.updateCustomerSelection(this.customer);
    let listAllCustomers = this.notifyService.getListAllCustomers();
    listAllCustomers.push(model);
    this.notifyService.updateAllCustomerList(listAllCustomers);
  }

  private removeCustomerAfterDelete() {
    let listCustomers = this.notifyService.getListAllCustomers();
    const index = listCustomers.indexOf(this.customer, 0);
    if (index > -1) {
      listCustomers.splice(index, 1);
    }
    this.notifyService.updateAllCustomerList(listCustomers);

    // Select the current 'customer', then navigate to 'customer-details', i.e. to load and display the list of all vessels after deletion.
    let customerModel = this.notifyService.getSelectedCustomer();
    this.notifyService.updateCustomerSelection(customerModel); //update the same customer selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['customers']);
  }

  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole customer will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteCustomer();
            }
          };
        });
    }
  }



}
